import { Button, Select } from 'antd';
import dayjs from 'dayjs';
import {
  Building2,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  LogIn,
  RotateCcw,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useGetAllIndustriesQuery } from '../../../app/features/onboarding/onboarding.api';
import LoginModal from '../../../components/LoginModal';
import { useAppFilters } from '../../../hooks';
import useAuth from '../../../hooks/useAuth';
import useDiscloser from '../../../hooks/useDiscloser';
import cn from '../../../lib/cn';
import { panelClass } from './portal';

type Range = '' | '7_days' | '1_month' | '6_months';

const POSTED_OPTIONS: { label: string; value: Range }[] = [
  { label: 'Any time', value: '' },
  { label: 'Last 7 days', value: '7_days' },
  { label: 'Last 30 days', value: '1_month' },
  { label: 'Last 6 months', value: '6_months' },
];

const DEADLINE_OPTIONS: { label: string; value: Range }[] = [
  { label: 'Any time', value: '' },
  { label: 'Within 7 days', value: '7_days' },
  { label: 'Within 30 days', value: '1_month' },
  { label: 'Within 6 months', value: '6_months' },
];

const shift = (value: Range, direction: 1 | -1) => {
  const date = dayjs();
  const amount =
    value === '7_days'
      ? date.add(7 * direction, 'day')
      : value === '1_month'
        ? date.add(direction, 'month')
        : date.add(6 * direction, 'month');
  return amount.format('YYYY-MM-DD');
};

const FilterSection = ({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) => (
  <section className="px-5 py-5">
    <h3 className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-gray-900">
      <Icon className="h-4 w-4 text-gray-400" strokeWidth={1.75} />
      {title}
    </h3>
    {children}
  </section>
);

/** Single-choice list styled as radio rows. */
const RadioList = ({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: { label: string; value: Range }[];
  value: Range;
  onChange: (value: Range) => void;
}) => (
  <div role="radiogroup" aria-label={name} className="-mx-2 space-y-0.5">
    {options.map((option) => {
      const checked = value === option.value;
      return (
        <button
          key={option.label}
          role="radio"
          aria-checked={checked}
          onClick={() => onChange(option.value)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
            checked
              ? 'bg-theme-primary/[0.06] font-medium text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
          )}
        >
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
              checked ? 'border-theme-primary' : 'border-gray-300 bg-white',
            )}
          >
            {checked && (
              <span className="h-2 w-2 rounded-full bg-theme-primary" />
            )}
          </span>
          {option.label}
        </button>
      );
    })}
  </div>
);

const Filters = () => {
  const { verifiedInfo } = useAuth();
  const [config, handleOpen, handleClose] = useDiscloser<'LOGIN'>();
  const { filters, handleFilter } = useAppFilters('job-portal-filters');
  const [posted, setPosted] = useState<Range>('');
  const [deadline, setDeadline] = useState<Range>('');
  // Collapsed on small screens so the job list stays near the top.
  const [mobileOpen, setMobileOpen] = useState(false);
  const today = dayjs().format('YYYY-MM-DD');

  const { data: industriesData } = useGetAllIndustriesQuery({});

  const handlePosted = (value: Range) => {
    setPosted(value);
    handleFilter('publish_date_start', value ? shift(value, -1) : undefined);
    handleFilter('publish_date_end', value ? today : undefined);
  };

  const handleDeadline = (value: Range) => {
    setDeadline(value);
    handleFilter('deadline_start', value ? today : undefined);
    handleFilter('deadline_end', value ? shift(value, 1) : undefined);
  };

  const activeCount = [filters.industry, posted, deadline].filter(
    Boolean,
  ).length;

  const clearAll = () => {
    handleFilter('industry', undefined);
    handlePosted('');
    handleDeadline('');
  };

  return (
    <div className="space-y-4 lg:sticky lg:top-24">
      <div className={cn(panelClass, 'overflow-hidden')}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 bg-gray-50/70 px-5 py-3.5">
          <button
            onClick={() => setMobileOpen((open) => !open)}
            aria-expanded={mobileOpen}
            className="flex flex-1 items-center gap-2 text-left text-sm font-semibold text-gray-900 lg:pointer-events-none"
          >
            <SlidersHorizontal className="h-4 w-4 text-gray-500" />
            Filters
            {activeCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-theme-primary px-1.5 text-[11px] font-semibold text-white">
                {activeCount}
              </span>
            )}
            <ChevronDown
              className={cn(
                'ml-auto h-4 w-4 text-gray-400 transition-transform lg:hidden',
                mobileOpen && 'rotate-180',
              )}
            />
          </button>
          {activeCount > 0 && (
            <button
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-theme-primary"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
          )}
        </div>

        <div
          className={cn(
            'divide-y divide-gray-100 border-t border-gray-200',
            !mobileOpen && 'hidden lg:block',
          )}
        >
          <FilterSection icon={Building2} title="Industry">
            <Select
              allowClear
              showSearch
              placeholder="All industries"
              value={filters.industry}
              onChange={(value) => handleFilter('industry', value)}
              options={
                industriesData?.data?.map((i) => ({
                  label: i.name,
                  value: i.name,
                })) || []
              }
              className="h-10 w-full [&_.ant-select-selection-placeholder]:!text-gray-400 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!bg-white"
            />
          </FilterSection>

          <FilterSection icon={CalendarDays} title="Date posted">
            <RadioList
              name="Date posted"
              options={POSTED_OPTIONS}
              value={posted}
              onChange={handlePosted}
            />
          </FilterSection>

          <FilterSection icon={CalendarClock} title="Application deadline">
            <RadioList
              name="Application deadline"
              options={DEADLINE_OPTIONS}
              value={deadline}
              onChange={handleDeadline}
            />
          </FilterSection>
        </div>
      </div>

      {!verifiedInfo && (
        <div className="hidden rounded-2xl border border-theme-primary/20 bg-theme-primary/[0.04] p-5 lg:block">
          <p className="text-sm font-semibold text-gray-900">
            Track your applications
          </p>
          <p className="mt-1 text-[13px] leading-5 text-gray-500">
            Log in to apply in one click and follow every stage, from screening
            to offer letter.
          </p>
          <Button
            block
            onClick={() => handleOpen({ type: 'LOGIN' })}
            className="btn-primary mt-4 h-9 rounded-full font-medium"
          >
            <LogIn className="h-4 w-4" />
            Log in
          </Button>
          <LoginModal
            data={config.data}
            type={config.type}
            handleClose={handleClose}
            open={config.open && config.type === 'LOGIN'}
          />
        </div>
      )}
    </div>
  );
};

export default Filters;
